/**
 * Loan amortization math.
 *
 * The amortization schedule is built using the standard "uniform
 * monthly period" convention — every payment accrues exactly one
 * month of interest at `annualRate / 12`, regardless of the actual
 * day count between due dates. That matches what an operator sees on
 * Bankrate / a typical mortgage calculator, and keeps the table
 * tidy. The actual "paid on" date (tracked elsewhere) is purely a
 * record-keeping field and does NOT influence interest accrual.
 *
 * All money is handled in integer cents to avoid floating-point
 * drift over the life of the loan. The standard payment is computed
 * once at full float precision, rounded to cents, and then the
 * last payment is adjusted to zero out any rounding residue against
 * the remaining balance.
 *
 * Date inputs are ISO 'YYYY-MM-DD' strings (local-calendar, no
 * timezone). Using strings makes (de)serialization to localStorage /
 * Supabase trivial and avoids the "Date() at midnight UTC vs local
 * midnight" off-by-one trap that bites date-only fields.
 */

export interface LoanConfig {
  /** Original principal in cents. */
  principalCents: number;
  /** Annual interest rate as a decimal (0.08 = 8%). */
  annualRate: number;
  /** Loan term in months. */
  termMonths: number;
  /** ISO 'YYYY-MM-DD' — the date the loan starts accruing. */
  effectiveDate: string;
  /** ISO 'YYYY-MM-DD' — when payment #1 is owed. May be off-cycle. */
  firstPaymentDate: string;
  /** Day-of-month (1–28) used for payments 2..n. */
  paymentDayOfMonth: number;
  /** Display label (e.g. "Family loan"). */
  label: string;
  /** Lender display name (e.g. family member, bank). */
  lender?: string;
}

export interface ScheduledPayment {
  /** 1-indexed payment number. */
  number: number;
  /** ISO 'YYYY-MM-DD'. */
  dueDate: string;
  /** Total payment in cents (principal + interest). */
  paymentCents: number;
  /** Principal portion in cents. */
  principalCents: number;
  /** Interest portion in cents. */
  interestCents: number;
  /** Balance remaining AFTER this payment, in cents. */
  remainingBalanceCents: number;
}

/**
 * Standard monthly amortization payment, in cents.
 *
 *   payment = P · r · (1+r)^n / ((1+r)^n − 1)
 *
 * where r = monthly rate and n = number of months.
 *
 * Returns 0 if the loan has no principal or no term so callers can
 * defensively render an empty schedule rather than NaN'ing out.
 */
export function computeMonthlyPaymentCents(
  principalCents: number,
  annualRate: number,
  termMonths: number,
): number {
  if (principalCents <= 0 || termMonths <= 0) return 0;
  if (annualRate === 0) return Math.round(principalCents / termMonths);
  const r = annualRate / 12;
  const factor = Math.pow(1 + r, termMonths);
  const payment = (principalCents * r * factor) / (factor - 1);
  return Math.round(payment);
}

/**
 * Build the full amortization schedule. The last payment is forced
 * to zero out the balance so any cent of rounding noise doesn't
 * leave the loan with a fractional residue.
 */
export function buildSchedule(config: LoanConfig): ScheduledPayment[] {
  const { principalCents, annualRate, termMonths } = config;
  if (principalCents <= 0 || termMonths <= 0) return [];

  const monthlyRate = annualRate / 12;
  const paymentCents = computeMonthlyPaymentCents(
    principalCents,
    annualRate,
    termMonths,
  );

  const schedule: ScheduledPayment[] = [];
  let balanceCents = principalCents;

  for (let i = 0; i < termMonths; i++) {
    const dueDate = i === 0
      ? config.firstPaymentDate
      : addMonthsOnDay(config.firstPaymentDate, i, config.paymentDayOfMonth);

    const interestCents = Math.round(balanceCents * monthlyRate);
    let principalPortion: number;
    let total: number;

    if (i === termMonths - 1) {
      // Final payment — close out whatever balance remains so we
      // never leave the loan with a 1-cent residue from rounding.
      principalPortion = balanceCents;
      total = balanceCents + interestCents;
    } else {
      principalPortion = paymentCents - interestCents;
      total = paymentCents;
      // Pathological case: interest exceeds payment (very long
      // term + very high rate). Cap principal at zero so the
      // balance never grows.
      if (principalPortion < 0) principalPortion = 0;
    }

    balanceCents -= principalPortion;
    schedule.push({
      number: i + 1,
      dueDate,
      paymentCents: total,
      principalCents: principalPortion,
      interestCents,
      remainingBalanceCents: Math.max(0, balanceCents),
    });
  }

  return schedule;
}

/**
 * Add N whole months to an ISO date, then snap to `day`. Months are
 * walked in the local calendar (no timezone shifting). If the
 * resulting month is shorter than `day` (e.g. asking for the 31st
 * of February), the date clamps to the last day of that month — the
 * behaviour every spreadsheet uses for amortization.
 */
export function addMonthsOnDay(
  isoDate: string,
  monthsToAdd: number,
  day: number,
): string {
  const [year, month] = parseISODate(isoDate);
  const targetYear = year + Math.floor((month - 1 + monthsToAdd) / 12);
  const targetMonth = ((month - 1 + monthsToAdd) % 12 + 12) % 12 + 1;
  const lastDayOfMonth = daysInMonth(targetYear, targetMonth);
  const clamped = Math.min(Math.max(1, day), lastDayOfMonth);
  return formatISODate(targetYear, targetMonth, clamped);
}

function parseISODate(iso: string): [number, number, number] {
  const [y, m, d] = iso.split('-').map((s) => Number.parseInt(s, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    throw new Error(`Invalid ISO date: "${iso}"`);
  }
  return [y, m, d];
}

function formatISODate(year: number, month: number, day: number): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * Parse an ISO 'YYYY-MM-DD' into a local-midnight Date for display
 * formatting. Doing this manually (vs `new Date(iso)`) avoids the
 * UTC-midnight reinterpretation that drops the date back one day
 * for any timezone west of UTC.
 */
export function isoToLocalDate(iso: string): Date {
  const [y, m, d] = parseISODate(iso);
  return new Date(y, m - 1, d);
}

/**
 * Today's date as 'YYYY-MM-DD' in the user's local timezone. Useful
 * as the default value when the operator marks a payment paid.
 */
export function todayISO(): string {
  const now = new Date();
  return formatISODate(now.getFullYear(), now.getMonth() + 1, now.getDate());
}
